import { writable } from 'svelte/store';

export type UnitPreference = 'metric' | 'imperial';

// Initialize from localStorage if available, otherwise default to metric
const storedPreference = typeof localStorage !== 'undefined' ? (localStorage.getItem('unit_preference') as UnitPreference) : null;
const initialValue: UnitPreference = storedPreference && ['metric', 'imperial'].includes(storedPreference) ? storedPreference : 'metric';

export const unitPreference = writable<UnitPreference>(initialValue);

// Subscribe to changes and persist to localStorage
if (typeof localStorage !== 'undefined') {
  unitPreference.subscribe(value => {
    localStorage.setItem('unit_preference', value);
  });
}

/**
 * Format volume natively scaling from DB Liters into target preference
 * @param liters Total Flow in Liters
 * @returns Formatted volume string
 */
export function formatVolume(liters: number | null, pref: UnitPreference): string {
    if (liters == null) return '—';
    if (pref === 'imperial') {
        const gal = liters * 0.264172;
        return `${Number(gal.toFixed(1)).toLocaleString()} gal`;
    }
    return `${Number(liters.toFixed(1)).toLocaleString()} L`;
}

/**
 * Format flow rate scaling from L/h into target preference
 * @param lph Flow rate in L/h
 * @returns Formatted rate string
 */
export function formatFlowRate(lph: number | null, pref: UnitPreference): string {
    if (lph == null) return '—';
    if (pref === 'imperial') {
        const gpm = lph * 0.00440287; // L/h to GPM
        return `${gpm.toFixed(2)} GPM`;
    }
    return `${lph.toFixed(1)} L/h`;
}

/**
 * Format pressure from kPa into target preference
 * @param kpa Pressure in kPa
 * @returns Formatted pressure string
 */
export function formatPressure(kpa: number | null, pref: UnitPreference): string {
    if (kpa == null) return '—';
    if (pref === 'imperial') {
        const psi = kpa * 0.145038;
        return `${psi.toFixed(1)} PSI`;
    }
    return `${kpa.toFixed(1)} kPa`;
}

/**
 * Format temperature from °C into target preference
 * @param celsius Temperature in °C
 * @returns Formatted temperature string
 */
export function formatTemperature(celsius: number | null, pref: UnitPreference): string {
    if (celsius == null) return '—';
    if (pref === 'imperial') {
        const f = celsius * 9 / 5 + 32;
        return `${f.toFixed(1)}°F`;
    }
    return `${celsius.toFixed(1)}°C`;
}
